import React, { useState, useEffect, useRef } from 'react'
import { Input, InputGroup } from 'components/input'
import { Select } from 'components/select'
import { Divider } from 'components/divider'
import { Badge } from 'components/badge'
import { useNavigate } from 'react-router-dom'
import { useLazyQuery } from '@apollo/client'
import { t } from 'i18next'
import { Pagination, PaginationList, PaginationNext, PaginationPage, PaginationPrevious } from 'components/pagination'
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from 'components/dropdown'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/table'
import { MagnifyingGlassIcon, EllipsisVerticalIcon } from '@heroicons/react/16/solid'
import { EyeIcon, EyeSlashIcon, ArrowLeftIcon, CalendarDaysIcon } from '@heroicons/react/20/solid'
import { ErrorDialog, SuccessDialog, ConfirmDialog } from 'components/custom/ResponseDialog'
import { UsersIcon } from 'components/icons'
import { GET_USER_LIST } from 'graphql/users/queries'
import { ImageDisplay } from 'components/custom/ImageDisplay'
import placeHolderImage from 'assets/images/users/profile_default.png'
import { getPageRange } from 'utils/paginationHelper'

// App Users module imports
import { APP_USER_POOL, DEFAULT_PAGE_SIZE, DEFAULT_PAGE, DEFAULT_USER_FILTER } from './constants'
import { getUserFullName, getUserStatusBadgeColor, getUserStatusText } from './helpers'
import { LoadingState, ErrorState, EmptyState, LoadingOverlay } from './components'
import useUserStatus from './hooks/useUserStatus'

export default function AppUsers() {
    const navigate = useNavigate()
    const [filterObj, setFilterObj] = useState(DEFAULT_USER_FILTER)
    const [searchString, setSearchString] = useState('')
    const searchStringRef = useRef(searchString)

    const [userList, setUserList] = useState(null)
    const [userCount, setUserCount] = useState(0)
    
    // Pagination
    const [page, setPage] = useState(DEFAULT_PAGE)
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE)
    const totalPages = Math.ceil(userCount / rowsPerPage)

    // User status hook
    const {
        showErrDialog,
        showSucDialog,
        showConfirmDialog,
        errorMessage,
        successMessage,
        confirmMessage,
        statusUpdateLoading,
        handleDisableUser,
        handleConfirmPositive,
        handleConfirmNegative,
        handleErrClose,
        handleSucClose,
    } = useUserStatus(() => searchUsers(filterObj))

    useEffect(() => {
        searchStringRef.current = searchString
    }, [searchString])

    const [getUserList, { loading: userListLoading, error: userListError }] = useLazyQuery(GET_USER_LIST, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data.searchUser.success && data.searchUser.code === '200') {
                setUserList(data.searchUser.users)
                setUserCount(data.searchUser.total_count)
            }
        },
    })

    useEffect(() => {
        searchUsers(filterObj)
    }, [page, rowsPerPage])

    const handlePageChange = newPage => {
        setPage(newPage)
    }

    // Search users - always use 'app' user pool
    const searchUsers = filter => {
        getUserList({
            variables: {
                searchUserId: null,
                userPool: APP_USER_POOL,
                role: null,
                page: {
                    offset: page * rowsPerPage,
                    limit: rowsPerPage,
                },
                searchString: searchStringRef.current,
                isActive: filter.active,
            },
        })
    }

    const onClickSearch = () => {
        setPage(0)
        setRowsPerPage(10)
        searchUsers(filterObj)
    }

    const onClickSearchClear = () => {
        setSearchString('')
        setFilterObj(DEFAULT_USER_FILTER)
        setPage(DEFAULT_PAGE)
        setRowsPerPage(DEFAULT_PAGE_SIZE)
        setTimeout(() => {
            searchUsers(DEFAULT_USER_FILTER)
        }, 100)
    }

    const handleViewBookings = (user) => {
        navigate('/app-users/bookings', {
            state: {
                userEmail: user.email,
                userName: getUserFullName(user),
                userId: user.id
            }
        })
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => {
                        navigate('/')
                    }}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <div>
                        <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap justify-between">
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                                <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1] w-[40px] h-[40px]">
                                        <UsersIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                                    </div>
                                    <div>
                                        <span className="ms-3">{t('App Users')}</span>
                                        <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                                            {t('View and manage mobile app users and their bookings')}
                                        </p>
                                    </div>
                                </h1>
                            </div>
                        </div>
                    </div>
                    <div className="mt-8 mb-4 flex sm:flex-row flex-wrap w-full gap-4">
                        <div>
                            <label htmlFor="status" className="block text-sm font-medium leading-6 text-gray-900">
                                {t('Status')}
                            </label>
                            <Select
                                name="active"
                                value={filterObj.active}
                                onChange={e => {
                                    let value
                                    if (e.target.value === 'true') {
                                        value = true
                                    } else if (e.target.value === 'false') {
                                        value = false
                                    } else {
                                        value = null
                                    }
                                    setFilterObj({ ...filterObj, active: value })
                                }}
                            >
                                <option value={null}>{t('All')}</option>
                                <option value="true">{t('Active')}</option>
                                <option value="false">{t('Inactive')}</option>
                            </Select>
                        </div>
                        <div className="flex-2">
                            <label htmlFor="search" className="block text-sm font-medium leading-6 text-gray-900">
                                {t('Search')}
                            </label>
                            <InputGroup>
                                <MagnifyingGlassIcon />
                                <Input
                                    name="search"
                                    id="search"
                                    value={searchString}
                                    onChange={e => setSearchString(e.target.value)}
                                    placeholder={t('Search by name, email, phone')}
                                />
                            </InputGroup>
                        </div>
                        <div className="flex-none grow space-x-2 justify-end place-content-end">
                            <button
                                type="button"
                                onClick={e => {
                                    e.stopPropagation()
                                    onClickSearch()
                                }}
                                className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                            >
                                {t('Search')}
                            </button>
                            <button
                                type="button"
                                onClick={e => {
                                    e.stopPropagation()
                                    onClickSearchClear()
                                }}
                                className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                                {t('Clear')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <Divider />

            {userListLoading && <LoadingState />}
            {userListError && <ErrorState message={t('Failed to fetch users!')} />}
            {!userListLoading && !userListError && userList?.length === 0 && <EmptyState />}
            {!userListLoading && !userListError && userList?.length > 0 && (
                <>
                    <div className="table-custom overflow-x-scroll lg:overflow-hidden rounded-b-lg bg-white p-0 flex">
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{t('Photo')}</TableHeader>
                                    <TableHeader>{t('Name')}</TableHeader>
                                    <TableHeader>{t('Email')}</TableHeader>
                                    <TableHeader>{t('Mobile')}</TableHeader>
                                    <TableHeader>{t('Status')}</TableHeader>
                                    <TableHeader>{t('Active')}</TableHeader>
                                    <TableHeader>{t('Actions')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {userList.map(user => (
                                    <TableRow
                                        key={user.id}
                                        className="cursor-pointer hover:bg-gray-50"
                                        onClick={() => handleViewBookings(user)}
                                        title={`User #${user.id}`}
                                    >
                                        <TableCell>
                                            <ImageDisplay
                                                s3Key={user.additional_info?.profile_photo?.s3_key}
                                                file=""
                                                placeHolderImage={placeHolderImage}
                                                classStyle="w-[50px] h-[50px] sm:w-[30px] sm:h-[30px] rounded-full ring-4 ring-white max-w-none"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex gap-2 items-center">
                                                {user.user_info?.given_name || ''} {user.user_info?.family_name || ''}
                                            </div>
                                        </TableCell>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>{user.user_info?.phone_number || '-'}</TableCell>
                                        <TableCell>
                                            <Badge color="lime">{user.cognito_status}</Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Badge color={getUserStatusBadgeColor(user.is_active)}>
                                                {getUserStatusText(user.is_active)}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Dropdown>
                                                <DropdownButton
                                                    plain
                                                    aria-label="More options"
                                                    onClick={e => e.stopPropagation()}
                                                    className="cursor-pointer"
                                                >
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom">
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(`/app-users/user-details/${user.id}`)
                                                        }}
                                                    >
                                                        <EyeIcon />
                                                        <DropdownLabel>{t('View')}</DropdownLabel>
                                                    </DropdownItem>
                                                    <DropdownDivider />
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            handleViewBookings(user)
                                                        }}
                                                    >
                                                        <CalendarDaysIcon />
                                                        <DropdownLabel>{t('View Bookings')}</DropdownLabel>
                                                    </DropdownItem>
                                                    <DropdownDivider />
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            handleDisableUser(user.id, !user.is_active)
                                                        }}
                                                    >
                                                        <EyeSlashIcon />
                                                        <DropdownLabel>
                                                            {user.is_active ? t('Disable') : t('Enable')}
                                                        </DropdownLabel>
                                                    </DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <Divider />
                    <Pagination className="py-6">
                        <PaginationPrevious
                            onClick={() => handlePageChange(page > 0 ? page - 1 : 0)}
                            disabled={page === 0}
                        />
                        <PaginationList>
                            {getPageRange(page, totalPages).map(index => (
                                <PaginationPage
                                    key={index}
                                    current={index === page}
                                    onClick={() => handlePageChange(index)}
                                >
                                    {index + 1}
                                </PaginationPage>
                            ))}
                        </PaginationList>
                        <PaginationNext
                            onClick={() => handlePageChange(page < totalPages - 1 ? page + 1 : totalPages - 1)}
                            disabled={page === totalPages - 1}
                        />
                    </Pagination>
                    <Divider />
                </>
            )}

            {showErrDialog && (
                <ErrorDialog
                    status={true}
                    title={t('Error')}
                    message={errorMessage}
                    onClose={handleErrClose}
                    isMentee={false}
                />
            )}
            {showSucDialog && (
                <SuccessDialog status={true} title={t('Success')} message={successMessage} onClose={handleSucClose} />
            )}

            {showConfirmDialog && (
                <ConfirmDialog
                    status={true}
                    title={t('User Status Update')}
                    negativeActionText={t('Cancel')}
                    positiveActionText={t('Yes')}
                    message={confirmMessage}
                    onPositive={handleConfirmPositive}
                    onNegative={handleConfirmNegative}
                />
            )}
            {statusUpdateLoading && <LoadingOverlay />}
        </>
    )
}
